import React from 'react';
import classNames from 'classnames';
import {TaxonomyNode} from 'sections/types';
import {Link} from '@shopify/polaris';
import {KeyCodes} from 'global-constants';
import {Routes, fetchTaxonomy, sendGoogleAnalytics} from '../../utilities';
import {
  GoogleAnalyticsEventActions,
  GoogleAnalyticsEventType,
} from '../../utilities/constants';

import styles from './NavigationDesktop.scss';

const PopoverColumns = {
  maxColumns: 3,
  itemsPerColumn: 7,
};

interface OwnProps {
  navItems: TaxonomyNode[];
}

interface State {
  activePopover?: string;
}

export default class NavigationDesktop extends React.PureComponent<
  OwnProps,
  State
> {
  state: State = {};
  taxonomy = fetchTaxonomy();

  toggleActivePopover = (handle: string) => {
    const {activePopover} = this.state;

    this.setState({
      activePopover: activePopover === handle ? undefined : handle,
    });
  };

  closeActivePopover = () => {
    this.setState({
      activePopover: undefined,
    });
  };

  onServiceClick = (serviceHandle: string) => {
    return () => {
      this.closeActivePopover();
      sendGoogleAnalytics({
        type: GoogleAnalyticsEventType.Event,
        payload: {
          eventAction: GoogleAnalyticsEventActions.navigationClick,
          eventLabel: serviceHandle,
        },
      });
    };
  };

  onMouseEnter = (handle: string) => {
    return () => {
      this.toggleActivePopover(handle);
    };
  };

  onKeyUp = (handle: string) => {
    const keyCodesList = [KeyCodes.enter, KeyCodes.spaceBar, KeyCodes.esc];

    return (evt: React.KeyboardEvent<HTMLButtonElement | HTMLDivElement>) => {
      const keyCode = evt.nativeEvent.keyCode;

      if (!keyCodesList.includes(keyCode)) return;

      if (keyCode === KeyCodes.esc) {
        this.closeActivePopover();
        return;
      }

      this.toggleActivePopover(handle);
    };
  };

  render() {
    const {navItems} = this.props;

    return (
      <div className={styles.NavigationContainer}>
        <div className={styles.Navigation}>
          <nav>
            <ul className={styles.NavigationList}>
              {navItems.map(item => {
                return (
                  <li
                    key={item.service.handle}
                    className={styles.NavigationListItem}
                  >
                    {this.renderPopoverContainer(item)}
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    );
  }

  renderPopoverContainer = (item: TaxonomyNode) => {
    return (
      <div
        className={styles.PopoverHoverTrigger}
        onMouseEnter={this.onMouseEnter(item.service.handle)}
        onMouseLeave={this.closeActivePopover}
        aria-controls={`Popover--${item.service.handle}`}
        aria-expanded={this.isActivePopover(item.service.handle)}
      >
        <button
          className={styles.PopoverButtonTrigger}
          onKeyUp={this.onKeyUp(item.service.handle)}
          aria-controls={item.service.handle}
          aria-expanded={this.isActivePopover(item.service.handle)}
          type="button"
        >
          {item.service.shortLabel}
        </button>
        {this.renderPopover(item)}
      </div>
    );
  };

  renderPopover = (item: TaxonomyNode) => {
    const bgImage = Routes.imagePath(`svg/${item.service.handle}.svg`);
    const bgImageClass = `PopoverBackgroundImage--${item.service.handle}`;
    const colsClass = this.generatePopoverClass(item);

    const className = classNames(
      styles.Popover,
      colsClass,
      bgImageClass,
      this.isActivePopover(item.service.handle) && styles.PopoverVisible
    );

    return (
      <div
        id={`Popover--${item.service.handle}`}
        className={className}
        onKeyUp={this.onKeyUp(item.service.handle)}
        style={{
          backgroundImage: `url(${bgImage})`,
        }}
      >
        {this.renderPopoverContent(item)}
      </div>
    );
  };

  renderPopoverContent = (item: TaxonomyNode) => {
    return item.children ? (
      <ul className={styles.PopoverList}>{this.renderChildList(item)}</ul>
    ) : null;
  };

  renderChildList(item: TaxonomyNode) {
    return item.children.map(item => (
      <li key={item.service.handle} className={styles.PopoverListItem}>
        <Link
          url={Routes.servicePath(this.taxonomy, item.service.handle)}
          onClick={this.onServiceClick(item.service.handle)}
        >
          {item.service.shortLabel}
        </Link>
        {item.children && (
          <ul className={styles.PopoverChildList}>
            {this.renderChildList(item)}
          </ul>
        )}
      </li>
    ));
  }

  isActivePopover = (handle: string) => {
    return this.state.activePopover === handle;
  };

  generatePopoverClass = (item: TaxonomyNode) => {
    const flattenedChildren = this.taxonomy.getFlattenedTaxonomyNode(item);
    const cols = Math.ceil(
      flattenedChildren.children.length / PopoverColumns.itemsPerColumn
    );
    const colsClass =
      cols > PopoverColumns.maxColumns ? PopoverColumns.maxColumns : cols;

    return `PopoverCols--${colsClass}`;
  };
}
